import { PlanetMark } from './BrandLogo.js';
import { isDemo } from './branding.js';
import { DemoBurger } from './DemoLogo.js';

import styles from './PlanetBurger.module.css';

/**
 * The menu toggle: three bars that resolve into the Planet Schwarz mark.
 *
 * Three layers, all in ONE coordinate system — the mark's own 100×72 box, at the
 * mark's own width. That is the whole trick, and it is what earlier versions got
 * wrong by drawing the intermediate shape in a 24-unit icon box: its ring came
 * out 15px narrower than the mark's and 2.5px higher, so the crossfade showed the
 * shape jumping and no amount of retiming could hide it.
 *
 *   bars     — three strokes, the closed state
 *   outline  — the mark's silhouette as a stroke: the same ellipse and circle,
 *              at the same coordinates, that PlanetMark fills
 *   mark     — the real PlanetMark
 *
 * So the sequence is: the bars converge into the silhouette, which is a real
 * transform of real geometry; then the gold floods into that silhouette without
 * anything moving at all, because the two are the same shape. The mark carries no
 * transform for exactly that reason — the fill has to be the one moment where
 * nothing shifts.
 *
 * The middle bar's handover is exact rather than approximate, and the corrected
 * artwork made it simpler: the ring is level and centred on y=36, which is where
 * the middle bar already sits. So the bar neither tilts nor moves — it only
 * widens to the ring's major axis, and is already lying along the ellipse that
 * replaces it.
 */
export function PlanetBurger({ open }: { open: boolean }): JSX.Element {
  // The fold is the component, and what it folds into is the Schwarz mark — the
  // one thing a demo build must not put on screen. So demo mode gives up the
  // morph rather than re-targeting it: bars that cross into an X, in the same
  // box at the same coordinates, so the button around them is unchanged.
  if (isDemo) return <DemoBurger open={open} />;

  return (
    <span className={`${styles.stack} ${open ? styles.open : ''}`}>
      <svg
        className={styles.bars}
        viewBox="0 0 100 72"
        fill="none"
        stroke="currentColor"
        strokeWidth={6}
        strokeLinecap="round"
        aria-hidden="true"
      >
        <line className={styles.barTop} x1="22" y1="22" x2="78" y2="22" />
        <line className={styles.barMid} x1="22" y1="36" x2="78" y2="36" />
        <line className={styles.barLow} x1="22" y1="50" x2="78" y2="50" />
      </svg>

      {/* The mark's silhouette, stroked. Same numbers as PlanetMark's mask. */}
      <svg
        className={styles.outline}
        viewBox="0 0 100 72"
        fill="none"
        stroke="currentColor"
        strokeWidth={5}
        aria-hidden="true"
      >
        <ellipse className={styles.outlineRing} cx="50" cy="36" rx="50" ry="10" />
        <circle className={styles.outlineDisc} cx="50" cy="36" r="33" />
      </svg>

      {/* Its own mask id: two lockups can be on screen at once, and duplicate ids
          make one of them mask against the other's shape. */}
      <PlanetMark className={styles.mark} maskId="ps-mark-toggle" />
    </span>
  );
}
